import type { SeatChangePreview } from '../../lib/billing.js';

/**
 * Pure decision helpers for the Manage seats dialog. These are the bits whose interactions kept
 * producing bugs (stale baselines, drift, commit gating), so they live here to be tested directly
 * rather than only through the component's effects.
 */

/**
 * "No change" is measured against Stripe's live count (the source of truth) once the preview has
 * loaded, falling back to the DB-sourced quantity only until then. So during DB/Stripe drift the
 * dialog reasons about the real current count, not a stale one.
 */
export function isNoChange(
  target: number,
  preview: SeatChangePreview | null,
  dbCurrentQuantity: number,
): boolean {
  return target === (preview?.currentQuantity ?? dbCurrentQuantity);
}

/**
 * A commit must be backed by a successful preview for the CURRENT target. A stale preview (for a
 * different target), a missing one, or a preview error all make it not ready, so the user can
 * never confirm a charge the modal didn't surface.
 */
export function isPreviewReady(
  target: number,
  preview: SeatChangePreview | null,
  previewError: boolean,
): boolean {
  return preview !== null && preview.targetQuantity === target && !previewError;
}

/**
 * The commit button is disabled while saving, when there is nothing to change,
 * or when no valid preview backs the current target.
 *
 * `loadingPreview` gates only a change that NEEDS the preview. The preview
 * prices workspace seats; an AI seat's price is on the Spec summary and never
 * touches Stripe, so a slow or hung preview was blocking a commit it has
 * nothing to say about. `previewReady` was already scoped to the workspace half
 * for exactly this reason and this was the half that stayed global.
 */
export function isCommitDisabled(args: {
  saving: boolean;
  loadingPreview: boolean;
  noChange: boolean;
  previewReady: boolean;
  /** Whether the workspace-seat half is part of this commit. */
  workspaceSeatsChanged: boolean;
}): boolean {
  const waitingOnPreview = args.workspaceSeatsChanged && args.loadingPreview;
  return args.saving || waitingOnPreview || args.noChange || !args.previewReady;
}

/**
 * One-shot drift correction: snap the stepper to Stripe's live count, but only before the user has
 * adjusted it (so we never yank their selection) and only when the loaded preview disagrees with
 * the current target.
 */
export function shouldRebaselineTarget(
  userAdjusted: boolean,
  preview: SeatChangePreview,
  target: number,
): boolean {
  return !userAdjusted && preview.currentQuantity !== target;
}

/**
 * What actually went wrong, rather than one guess for every failure.
 *
 * This banner used to say "check your connection" no matter what came back, so
 * a Stripe misconfiguration, a plan that cannot buy seats, and an admin
 * override all told a manager to retry -- and for the last two, retrying can
 * never work. The route's codes are the ones it really returns
 * (apps/api/src/routes/billing.ts): `stripe_misconfigured` (500),
 * `seats_not_pro` (403), `seats_overridden` (409), `invalid_quantity` (400).
 * Anything with no code at all never reached the API, which is the only case
 * where the connection is a fair thing to blame.
 */
export function previewErrorMessage(code: string): string {
  switch (code) {
    case 'stripe_misconfigured':
      return 'Billing is not fully configured, so the cost could not be calculated. This needs an admin, not a retry.';
    case 'seats_not_pro':
      return 'Extra seats require an active Pro subscription.';
    case 'seats_overridden':
      return 'Seats are set by an admin override and cannot be changed here.';
    case 'invalid_quantity':
      return 'That seat count is out of range.';
    case 'unreachable':
      return "We couldn't reach billing to calculate the cost. Check your connection and try again before confirming.";
    default:
      return "We couldn't calculate the cost right now. Try again before confirming.";
  }
}
